import { useMemo } from "react";
import { Stack, Tabs, Title } from "@mantine/core";
import { IconPlugConnected } from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";

import type { RouterContextType } from "@/api/permissions/permissions.api";
import { AlertsTab } from "@/components/features/FmIntegrations/AlertsTab";
import { ConnectionsTab } from "@/components/features/FmIntegrations/ConnectionsTab";
import { DashboardTab } from "@/components/features/FmIntegrations/DashboardTab";
import { FieldMappingTab } from "@/components/features/FmIntegrations/FieldMappingTab";
import { LogsTab } from "@/components/features/FmIntegrations/LogsTab";
import { useBreadcrumb } from "@/hooks/useBreadcrumb";
import { usePersistedTabs } from "@/hooks/usePersistedTabs";
import { authGuardWithPermissions } from "@/utils/authGuard";
import type { TabItemType } from "@/types/common.types";

export const Route = createFileRoute("/_authenticated/fm-integrations/")({
  component: FmIntegrationsPage,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

function FmIntegrationsPage() {
  const { activeTab, setActiveTab } = usePersistedTabs({ defaultTab: "dashboard" });

  useBreadcrumb([
    {
      title: "FM Integrations",
      href: "/fm-integrations",
      icon: <IconPlugConnected size={16} stroke={1.5} />,
    },
  ]);

  const tabsData: TabItemType[] = useMemo(
    () => [
      { value: "dashboard", label: "Dashboard", component: <DashboardTab /> },
      { value: "connections", label: "Connections", component: <ConnectionsTab /> },
      { value: "field-mapping", label: "Field Mapping", component: <FieldMappingTab /> },
      { value: "logs", label: "Logs", component: <LogsTab /> },
      { value: "alerts", label: "Alerts", component: <AlertsTab /> },
    ],
    [],
  );

  return (
    <Stack gap="md">
      <Title
        order={3}
        style={{
          fontWeight: "var(--font-weight-bold)",
          fontSize: "var(--heading-font-size-h3)",
        }}
      >
        FM Integrations
      </Title>
      <Tabs value={activeTab} onChange={setActiveTab} keepMounted={false}>
        <Tabs.List>
          {tabsData.map((tab) => (
            <Tabs.Tab key={tab.value} value={tab.value}>
              {tab.label}
            </Tabs.Tab>
          ))}
        </Tabs.List>

        {tabsData.map((tab) => (
          <Tabs.Panel key={tab.value} value={tab.value} pt="md">
            {tab.component}
          </Tabs.Panel>
        ))}
      </Tabs>
    </Stack>
  );
}
